import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { selectOrderDetails, selectOrderDetailsLoading } from 'redux/reducers/euDdp/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { EasCard } from 'ui/EasCard';

const vatValidatedChipColor = (
	validated: string | boolean | null | undefined,
): 'warning' | 'success' => {
	if (validated === 'not_validated' || validated === false) return 'warning';
	return 'success';
};

const vatValidatedChipLabel = (validated: string | boolean | null | undefined): string => {
	if (typeof validated === 'string') return validated.replace(/_/g, ' ');
	if (validated === true) return 'Validated';
	return 'Not validated';
};

export const DeliveryInformation: FC = () => {
	const order = useSelector(selectOrderDetails);
	const orderIsLoading = useSelector(selectOrderDetailsLoading);

	const address =
		[
			order?.order.delivery_address_line_1,
			order?.order.delivery_address_line_2,
			order?.order.delivery_postal_code,
			order?.order.delivery_city,
			order?.order.delivery_state_province,
		]
			.filter(Boolean)
			.join(', ') || '—';

	return (
		<div>
			{order && !orderIsLoading ? (
				<EasCard id="eu-ddp-order-delivery-information" title="Delivery Information">
					<Typography color="text.secondary">Customer:</Typography>
					<Typography variant="h6" component="div">
						{order.order.delivery_person || '—'}
					</Typography>

					{order.order.recipient_company_name && (
						<>
							<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
								Company name:
							</Typography>
							<Typography>{order.order.recipient_company_name}</Typography>
						</>
					)}
					{order.order.recipient_company_vat && (
						<>
							<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
								Company VAT number:
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
								<Typography>{order.order.recipient_company_vat}</Typography>
								<Chip
									label={vatValidatedChipLabel(order.order.recipient_company_vat_validated)}
									color={vatValidatedChipColor(order.order.recipient_company_vat_validated)}
									size="small"
									sx={{
										p: '0 5px',
										ml: 1,
										fontSize: '.7rem',
										height: '20px',
									}}
								/>
							</Box>
						</>
					)}

					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Email:
					</Typography>
					<Typography>{order.order.delivery_email || '—'}</Typography>

					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Phone number:
					</Typography>
					<Typography>{order.order.delivery_phone || '—'}</Typography>

					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Delivery Country:
					</Typography>
					<CountryWithFlag countryCode={order.order.delivery_country} />

					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Delivery Address:
					</Typography>
					<Typography>{address}</Typography>

					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Delivery Cost:
					</Typography>
					<AmountAndCurrency
						amount={order.order.delivery_cost ?? 0}
						currency={order.order.payment_currency}
						size={1}
					/>
				</EasCard>
			) : (
				<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px', mb: 2 }} />
			)}
		</div>
	);
};
